import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { invoiceService } from '../../../services/invoiceService';
import { reviewService } from '../../../services/reviewService';

const RentalHistoryCard = ({ rental }) => {
  const navigate = useNavigate();
  const [showRating, setShowRating] = useState(!rental?.rated);
  const [selectedRating, setSelectedRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(rental?.rated || false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleRatingSubmit = async () => {
    if (!selectedRating) return;
    setIsSubmittingRating(true);
    try {
      await reviewService?.submitReview({
        bookingId: rental?.id,
        overallRating: selectedRating,
        comment: ''
      });
      setRatingSubmitted(true);
      setShowRating(false);
    } catch (error) {
      console.error('Rating submission error:', error);
      setRatingSubmitted(true);
      setShowRating(false);
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      setIsDownloading(true);
      await invoiceService?.generatePDF(rental?.id, {
        title: rental?.trailerName,
        type: rental?.trailerType,
        location: rental?.location
      });
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download receipt. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleStartReturnInspection = () => {
    const params = new URLSearchParams({
      bookingId: rental?.id || '',
      ownerId: rental?.ownerId || '',
      trailerListingId: rental?.trailerListingId || rental?.trailerId || ''
    });
    navigate(`/trailer-return-inspection?${params?.toString()}`);
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden hover:shadow-elevation-3 transition-smooth">
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-40 h-40 sm:h-auto overflow-hidden flex-shrink-0">
          <Image
            src={rental?.trailerImage}
            alt={rental?.trailerImageAlt}
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="flex-1 p-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3">
            <div className="flex-1">
              <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                {rental?.trailerName}
              </h3>
              
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground mb-3">
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={14} strokeWidth={2} />
                  <span className="font-body">{formatDate(rental?.rentalDate)}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="ArrowRight" size={14} strokeWidth={2} />
                  <span className="font-body">{formatDate(rental?.returnDate)}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="DollarSign" size={14} strokeWidth={2} />
                  <span className="font-body font-medium text-foreground">${rental?.totalCost}</span>
                </div>
              </div>

              {/* Return Inspection Prompt for completed bookings */}
              {!rental?.returnInspectionDone && (
                <div className="bg-warning/10 border border-warning/20 rounded-md p-3 mb-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="ClipboardList" size={16} className="text-warning" strokeWidth={2} />
                    <p className="font-body text-sm text-warning font-medium">
                      Return inspection pending — Document trailer condition to close out this rental
                    </p>
                  </div>
                </div>
              )}

              {showRating && !ratingSubmitted && (
                <div className="bg-muted rounded-md p-3 mb-3">
                  <p className="font-body text-sm text-foreground mb-2">How was your experience?</p>
                  <div className="flex items-center space-x-2 mb-3">
                    {[1, 2, 3, 4, 5]?.map((star) => (
                      <button
                        key={star}
                        onClick={() => setSelectedRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="focus:outline-none transition-smooth"
                      >
                        <Icon
                          name="Star"
                          size={24}
                          strokeWidth={2}
                          className={`${
                            star <= (hoverRating || selectedRating)
                              ? 'text-accent fill-accent' : 'text-muted-foreground'
                          } transition-smooth`}
                        />
                      </button>
                    ))}
                  </div>
                  {selectedRating > 0 && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleRatingSubmit}
                      loading={isSubmittingRating}
                      iconName="Send"
                    >
                      Submit Rating
                    </Button>
                  )}
                </div>
              )}

              {ratingSubmitted && (
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="CheckCircle" size={16} className="text-success" strokeWidth={2} />
                  <span className="font-body text-sm text-success">Review submitted</span>
                </div>
              )}
            </div>

            <div className="flex sm:flex-col gap-2 mt-3 sm:mt-0 sm:ml-4">
              {!rental?.returnInspectionDone && (
                <Button
                  variant="default"
                  size="sm"
                  iconName="ClipboardList"
                  onClick={handleStartReturnInspection}
                  className="flex-1 sm:flex-none"
                >
                  Return Inspection
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                iconName="RotateCcw"
                onClick={() => navigate(`/trailer-detail/${rental?.trailerId || rental?.id}`)}
                className="flex-1 sm:flex-none"
              >
                Book Again
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="FileText"
                onClick={handleDownloadReceipt}
                loading={isDownloading}
                className="flex-1 sm:flex-none"
              >
                Receipt
              </Button>
              {!ratingSubmitted && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Star"
                  onClick={() => navigate(`/reviews-management?bookingId=${rental?.id}`)}
                  className="flex-1 sm:flex-none"
                >
                  Full Review
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalHistoryCard;